import { useRef, useEffect, useMemo, useCallback } from 'react';
import { X, Bell, Trash2, Check, Compass, UserPlus, Ship } from 'lucide-react';
import { Notification } from '../types/friends';
import { motion, AnimatePresence } from 'framer-motion';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';
import { debug } from '../utils/debug';

interface NotificationsDropdownProps {
  notifications: Notification[];
  isOpen: boolean;
  onClose: () => void;
  onMarkRead: (notificationId: string) => void;
  onMarkAllRead: () => void;
  onDelete: (notificationId: string) => void;
  onNotificationClick?: (notification: Notification) => void;
}

const formatCompactRelativeTime = (timestamp?: number | Date): string => {
  if (!timestamp) return 'just now';
  const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
  const diffMs = Date.now() - date.getTime();

  if (isNaN(diffMs) || diffMs < 45000) {
    return 'just now';
  }

  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 5) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
};

const NotificationsDropdown = ({
  notifications,
  isOpen,
  onClose,
  onMarkRead,
  onMarkAllRead,
  onDelete,
  onNotificationClick,
}: NotificationsDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { formatDate } = useDisplayPreferences();

  // Dynamically format any YYYY-MM-DD date pattern and modernize legacy phrasing in messages
  const formatNotificationMessage = useCallback((message: string): string => {
    if (!message) return '';
    const cleaned = message
      .replace(/tagged you on a cruise:/gi, 'invited you to join them on:')
      .replace(/tagged you on a cruise/gi, 'invited you to join them on a cruise');
    return cleaned.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (match, y, m, d) => {
      try {
        const dateObj = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        return formatDate(dateObj);
      } catch {
        return match;
      }
    });
  }, [formatDate]);

  // Compute clean, structured Option A Title and Subtitle for each notification type
  const getNotificationPresentation = useCallback(
    (notification: Notification): { title: string; subtitle: string } => {
      if (notification.title) {
        return {
          title: notification.title,
          subtitle: formatNotificationMessage(notification.message),
        };
      }

      // Extract ship name and departure date (supports top-level fields or embedded cruise data)
      const shipName = notification.shipName || notification.invitedCruiseData?.shipName || '';
      const rawDate = notification.sailingDate || notification.invitedCruiseData?.departureDate || '';
      let formattedDate = '';
      if (rawDate) {
        try {
          const [y, m, d] = rawDate.split('-').map((v) => parseInt(v, 10));
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            formattedDate = formatDate(new Date(y, m - 1, d));
          } else {
            formattedDate = rawDate;
          }
        } catch {
          formattedDate = rawDate;
        }
      }

      const shipAndDate = shipName
        ? formattedDate
          ? `${shipName} (${formattedDate})`
          : shipName
        : '';

      switch (notification.type) {
        case 'itinerary-update': {
          return {
            title: 'Port Schedule Update',
            subtitle: shipAndDate
              ? `The official port schedule for your ${shipAndDate} sailing has been updated.`
              : formatNotificationMessage(notification.message),
          };
        }
        case 'itinerary-available': {
          return {
            title: 'Official Itinerary Available',
            subtitle: shipAndDate
              ? `An official itinerary matching your ${shipAndDate} sailing is now available. Tap to link your cruise and sync port times.`
              : formatNotificationMessage(notification.message),
          };
        }
        case 'cruise-invite': {
          const sender = notification.fromDisplayName || 'A friend';
          return {
            title: 'Cruise Invitation',
            subtitle: shipAndDate
              ? `${sender} invited you to join them on ${shipAndDate}. Tap to view details and add to your sailings.`
              : `${sender} invited you to join them on a cruise. Tap to view details and add to your sailings.`,
          };
        }
        case 'friend-request': {
          const sender = notification.fromDisplayName || 'Someone';
          return {
            title: 'Friend Request',
            subtitle: `${sender} sent you a friend request to connect and share cruise itineraries. Tap to review.`,
          };
        }
        default: {
          return {
            title: notification.fromDisplayName || 'Notification',
            subtitle: formatNotificationMessage(notification.message),
          };
        }
      }
    },
    [formatDate, formatNotificationMessage]
  );

  // Separate and sort notifications: unread ("New") at top, read ("Reviewed") at bottom
  const { newNotifications, reviewedNotifications } = useMemo(() => {
    const unread: Notification[] = [];
    const read: Notification[] = [];
    for (const n of notifications) {
      if (n.read) {
        read.push(n);
      } else {
        unread.push(n);
      }
    }
    unread.sort((a, b) => b.createdAt - a.createdAt);
    read.sort((a, b) => b.createdAt - a.createdAt);
    return { newNotifications: unread, reviewedNotifications: read };
  }, [notifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      // If clicking the bell trigger button, ignore click-outside so button onClick can toggle
      const isTrigger = (target as Element)?.closest?.('[data-notification-trigger="true"]');
      if (isTrigger) {
        return;
      }

      const isInside = dropdownRef.current?.contains(target) ?? false;
      debug.log('[NotificationsDropdown] click-outside check', {
        isInside,
        hasRef: !!dropdownRef.current,
      });
      if (dropdownRef.current && !isInside) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderCategoryIcon = (type: Notification['type']) => {
    switch (type) {
      case 'itinerary-update':
      case 'itinerary-available':
        return (
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
            <Compass className="w-4 h-4" />
          </div>
        );
      case 'friend-request':
        return (
          <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
            <UserPlus className="w-4 h-4" />
          </div>
        );
      case 'cruise-invite':
        return (
          <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
            <Ship className="w-4 h-4" />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
            <Bell className="w-4 h-4" />
          </div>
        );
    }
  };

  const renderNotificationItem = (notification: Notification) => {
    const { title, subtitle } = getNotificationPresentation(notification);

    return (
      <div
        key={notification.id}
        role="button"
        tabIndex={0}
        className={`p-3.5 hover:bg-gray-50/80 cursor-pointer transition-colors ${
          !notification.read ? 'bg-blue-50/40' : 'bg-white'
        }`}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (onNotificationClick) {
              onNotificationClick(notification);
            }
            if (!notification.read) {
              onMarkRead(notification.id);
            }
          }
        }}
        onClick={() => {
          debug.log('[NotificationsDropdown] notification row clicked', {
            notificationId: notification.id,
            type: notification.type,
            hasOnNotificationClick: !!onNotificationClick,
          });
          if (onNotificationClick) {
            onNotificationClick(notification);
          }
          if (!notification.read) {
            onMarkRead(notification.id);
          }
        }}
      >
        <div className="flex items-start gap-3">
          {renderCategoryIcon(notification.type)}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-1.5">
              <p className="text-sm font-semibold text-gray-900 line-clamp-1 break-words">
                {title}
              </p>
              <span className="text-xs text-gray-400 shrink-0 font-medium">
                {formatCompactRelativeTime(notification.createdAt)}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-0.5 leading-snug break-words">
              {subtitle}
            </p>
          </div>
          <div className="flex items-center gap-0.5 shrink-0 self-center">
            {!notification.read && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkRead(notification.id);
                }}
                className="text-gray-400 hover:text-primary-600 hover:bg-primary-50 p-1.5 rounded-full transition-colors"
                title="Mark as reviewed"
                aria-label="Mark as reviewed"
              >
                <Check className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(notification.id);
              }}
              className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-full transition-colors"
              title="Delete notification"
              aria-label="Delete notification"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          ref={dropdownRef}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className="fixed sm:absolute top-[calc(var(--full-header-height,4rem)+0.5rem)] sm:top-full sm:mt-2 left-3 right-3 sm:left-auto sm:right-0 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[min(32rem,80vh)] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-gray-700" />
              <h3 className="text-base font-bold text-gray-800">
                Notifications
              </h3>
              {newNotifications.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-700 rounded-full">
                  {newNotifications.length} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {newNotifications.length > 0 && (
                <button
                  onClick={onMarkAllRead}
                  className="text-xs font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
                  title="Mark all as reviewed"
                >
                  Mark all reviewed
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain divide-y divide-gray-100">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium">No notifications</p>
                <p className="text-xs text-gray-400 mt-1">We'll alert you when there are updates.</p>
              </div>
            ) : (
              <>
                {/* "New" Section */}
                {newNotifications.length > 0 && (
                  <div>
                    <div className="px-4 py-1.5 bg-blue-50/80 border-b border-blue-100 flex items-center justify-between sticky top-0 z-10">
                      <span className="text-xs font-bold text-blue-800 uppercase tracking-wider">
                        New
                      </span>
                      <span className="text-xs font-medium text-blue-600">
                        {newNotifications.length} unread
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {newNotifications.map(renderNotificationItem)}
                    </div>
                  </div>
                )}

                {/* "Reviewed" Section */}
                {reviewedNotifications.length > 0 && (
                  <div>
                    <div className="px-4 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Reviewed
                      </span>
                      <span className="text-xs font-medium text-gray-400">
                        {reviewedNotifications.length} {reviewedNotifications.length === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {reviewedNotifications.map(renderNotificationItem)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationsDropdown;

